import React, { useEffect, useState } from 'react';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { evidenceSignedUrl } from '../../services/issueEvidenceService';

export const IssueEvidenceBoard: React.FC<{
  records: IssueEvidenceRecord[];
  editable: boolean;
  onAdd?: (file: File, caption: string) => Promise<void>;
  onDelete?: (record: IssueEvidenceRecord) => Promise<void>;
  resolveUrl?: (storagePath: string) => Promise<string>;
  emptyLabel?: string;
}> = ({ records, editable, onAdd, onDelete, resolveUrl = evidenceSignedUrl, emptyLabel = 'Nenhuma evidência de observação.' }) => {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState('');
  const [urls, setUrls] = useState<Record<string, string>>({});

  const imageKey = records.filter((record) => record.kind === 'image').map((record) => record.id).join('|');
  useEffect(() => {
    let cancelled = false;
    const images = records.filter((record) => record.kind === 'image');
    if (images.length === 0) return undefined;
    void Promise.all(images.map(async (record) => {
      try {
        const url = await resolveUrl(record.storage_path);
        return [record.id, url] as const;
      } catch {
        return [record.id, ''] as const;
      }
    })).then((pairs) => {
      if (cancelled) return;
      setUrls(Object.fromEntries(pairs.filter(([, url]) => url)));
    });
    return () => { cancelled = true; };
  }, [imageKey, records, resolveUrl]);

  const openFile = async (record: IssueEvidenceRecord) => {
    setError('');
    try {
      const url = urls[record.id] || await resolveUrl(record.storage_path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível abrir o arquivo.');
    }
  };

  return (
    <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Evidências</h3>
        {editable && onAdd ? (
          <button type="button" onClick={() => setOpen(true)} className="text-[12px] font-medium text-slate-700 hover:underline dark:text-slate-200">
            + Anexar
          </button>
        ) : null}
      </div>
      {records.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">{emptyLabel}</p> : (
        <ul className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {records.map((record) => (
            <li key={record.id} className="min-w-0 rounded border border-slate-100 p-2 dark:border-slate-800">
              {record.kind === 'image' && urls[record.id] ? (
                <img src={urls[record.id]} alt={record.caption || record.name} className="mb-1 max-h-36 w-full rounded object-contain" />
              ) : null}
              <p className="truncate text-[12px] font-medium text-slate-800 dark:text-slate-100">{record.name}</p>
              {record.caption ? <p className="text-[12px] text-slate-500">{record.caption}</p> : null}
              <div className="mt-1 flex gap-2 text-[12px]">
                <button type="button" onClick={() => void openFile(record)} className="font-medium text-slate-700 hover:underline">
                  {record.kind === 'image' ? 'Abrir imagem' : 'Abrir arquivo'}
                </button>
                {editable && onDelete ? (
                  <button type="button" onClick={() => void onDelete(record).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Não foi possível excluir.'))} className="text-slate-500 hover:underline">
                    Excluir
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {editable && open && onAdd ? (
        <form className="mt-2 flex flex-wrap items-center gap-2" onSubmit={(event) => {
          event.preventDefault();
          const input = event.currentTarget.elements.namedItem('evidence-file');
          const file = input instanceof HTMLInputElement ? input.files?.[0] : undefined;
          if (!file) {
            setError('Escolha uma imagem ou um arquivo.');
            return;
          }
          setError('');
          void onAdd(file, caption).then(() => {
            setCaption('');
            setOpen(false);
          }).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Não foi possível anexar.'));
        }}>
          <input name="evidence-file" aria-label="Arquivo de evidência" type="file" className="max-w-full text-[12px]" />
          <input aria-label="Legenda" value={caption} onChange={(event) => setCaption(event.target.value)} placeholder="Legenda" className="h-8 min-w-0 flex-1 rounded border border-slate-300 px-2 text-[12px]" />
          <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px] font-medium text-slate-700">Enviar</button>
          <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-slate-500">Cancelar</button>
        </form>
      ) : null}
      {error ? <p className="mt-1 text-[12px] text-red-700">{error}</p> : null}
    </section>
  );
};
